<script setup>

import BaseModalDialog from "@/components/common/Base/BaseModalDialog.vue";
import {computed, ref, watch} from "vue";
import ImportButton from "@/components/common/Buttons/ImportButton.vue";
import useCaseBulkImport from "@/components/Case/CaseBulkImport/composables/useCaseBulkImport";
import {numberFormatCompact} from "@/utils/filters";
import CheckboxWithIcon from "@/components/common/CheckboxWithIcon.vue";
import HeaderLined from "@/components/common/HeaderLined.vue";
import DataGrid from "@/components/DataGrid/DataGrid.vue";
import {useGetDataStatic} from "@/components/common/Composables/useGetDataStatic";
import BlockchainCryptoBadge from "@/components/common/BlockchainCryptoBadge.vue";
import CopyableItemWithTooltip from "@/components/Case/CaseBulkImport/CopyableItemWithTooltip.vue";
import CategoryBadge from "@/components/common/CategoryBadge.vue";
import store from "@/store";
import SmartTooltipGeneric from "@/components/common/SmartTooltips/SmartTooltipGeneric.vue";
import RouteToAddress from "@/components/common/RouteToAddress.vue";
import SmartTooltipBase from "@/components/common/SmartTooltips/SmartTooltipBase.vue";
import ActionConfirmDialog from "@/views/Addresses/Dialogs/ActionConfirmDialog.vue";
import TableItemsExpandable from "@/views/Addresses/TableItemsExpandable.vue";

const alerts = ref([])
const modalRef = ref(null)

const resetState = () => {
    alerts.value = []
    modalRef.value.setDisabled(false)
    resetInternalState()
    getInitialCategory()
}

const {
    onUpload,
    handleImport,
    propagateToCluster,
    propagateToSingletons,
    isVisible,
    isWarning,
    invalidFiles,
    resetState: resetInternalState,
    progress,
    globalCancelToken,
    isCanceled,
    casesEditedCount,
    casesCreatedCount,
    casesLoadedTotal,
    addressesToImportTotal,
    addressesImportedTotal,
    failedImports,
    isImportFinished,
    loading,
    isInputValid,
    warningItems,
    isFileUploadFinished,
    getNumberOfIgnoredAddresses,
    exportErrors
} = useCaseBulkImport()

const errorHeaders = [
    {text: 'File Name', align: 'start', value: 'name'},
    {text: 'Issues', align: 'start', value: 'issues'}
]

const warningHeaders = [
    {text: 'Title', align: 'start', value: 'title', sortable: false},
    {text: 'Source File', align: 'start', value: 'file', sortable: false},
    {text: 'Address', align: 'start', value: 'address', sortable: false},
    {text: 'Issue', align: 'start', value: 'issue', sortable: false},
]

const initPagination = {pagination: {page: 1, itemsPerPage: 10}}
const getErrorItems = useGetDataStatic(invalidFiles, initPagination)

const getWarningItems = useGetDataStatic(warningItems, initPagination)

const onFileLoad = async (fileList) => {
    if (fileList.length === 0) return
    resetState()
    await onUpload(fileList)

    if (invalidFiles.value?.length > 0) {
        tabs.value = 0
    } else if (warningItems.value?.length > 0) {
        tabs.value = 1
    }
}

const getFailedImports = useGetDataStatic(failedImports, initPagination)

const failedImportsHeaders = [
    {text: 'Case', align: 'start', value: 'title', sortable: false},
    {text: 'Currency', align: 'start', value: 'currency', sortable: false},
    {text: 'Addresses', align: 'start', value: 'addresses', sortable: false},
    {text: 'Source Files', align: 'start', value: 'files', sortable: false},
    {text: 'Error', align: 'start', value: 'error', sortable: false},
    {text: 'Action', align: 'start', value: 'action', sortable: false}
]

const onImport = async () => {
    modalRef.value.setDisabled(true)
    await handleImport(categoryName.value)

    if (isCanceled.value) return

    if (failedImports.value?.length > 0) {
        tabs.value = 2
    }

    modalRef.value.setDisabled(false)
    emit("import-finished")
}

const clearInput = (e) => e.target.value = ""

const tabs = ref(null)

const errorItemsColor = "error"
const warningItemsColor = "warning"
const failedImportsColor = "error"

watch([invalidFiles, warningItems, failedImports, isImportFinished, loading, isFileUploadFinished], () => {
    updateAlerts()
}, {deep: true})

const updateAlerts = () => {
    alerts.value = []

    if (loading.value) return

    const numberOfIgnoredAddresses = getNumberOfIgnoredAddresses()
    if (numberOfIgnoredAddresses > 0) {
        alerts.value.push({
            message: `Total addresses with no supported currencies: ${numberOfIgnoredAddresses}`,
            type: "warning"
        })
    }
    if (!isImportFinished.value && isFileUploadFinished.value) {
        alerts.value.push({
            message: `Case count for import: ${casesLoadedTotal.value}; address count for import: ${addressesToImportTotal.value}`,
            type: casesLoadedTotal.value ? "info" : "error"
        })
    }
    if (invalidFiles.value.length > 0) {
        alerts.value.push({
            message: "Some files caused unexpected errors. You can find more information under the Errors tab.",
            type: errorItemsColor
        })
    }
    if (warningItems.value.length > 0 && !isImportFinished.value) {
        alerts.value.push({
            message: "Minor issues were found. These are listed under the Warnings tab and will be ignored going forward.",
            type: warningItemsColor
        })
    }
    if (failedImports.value.length > 0) {
        alerts.value.push({
            message: "Some data failed to import. A list of them can be found under the Failed Imports tab.",
            type: failedImportsColor
        })
    }
    if (isImportFinished.value) {
        alerts.value.push({
            message: `Import finished. Cases created: ${casesCreatedCount.value}; Cases updated: ${casesEditedCount.value}; Addresses imported: ${addressesImportedTotal.value}`,
            type: "info"
        })
    }
}

const getInitialCategory = () => {
    const localStorageCategory = localStorage.getItem("bulkCaseImportCategory")
    if (localStorageCategory === "" || localStorageCategory === null)
        categoryName.value = "PČR"
    else
        categoryName.value = localStorageCategory;
}

const categoryName = ref()
getInitialCategory()

const handleCategoryChange = () => {
    localStorage.setItem("bulkCaseImportCategory", categoryName.value ?? "")
}

const categories = computed(() => store.getters.categories)
const categoryOfChoice = computed(() => {
    let category = categories.value?.find(cat => cat.name === categoryName.value);
    if (!category) {
        category = {
            id: null,
            name: categoryName.value,
            color: "#000000",
            icon: "mdi-help-circle-outline",
            secondaryColor: "#000000",
            exists: false
        }
    } else {
        category.exists = true
    }
    return category
})

const progressPercentage = computed(() => (progress.value.completeCount / progress.value.total) * 100)

const confirmCloseDialogRef = ref(null)

const closeConfirmed = (close) => {
    globalCancelToken.value.cancel()
    isCanceled.value = true
    close()
}

const closeModal = (close) => {
    if (loading.value) {
        confirmCloseDialogRef.value.open()
        return
    }
    closeConfirmed(close)
}

const emit = defineEmits(["import-finished"])

</script>

<template>
    <BaseModalDialog
        ref="modalRef"
        title="Case Bulk Importer"
        @load-modal-state-event="resetState"
        width="60%"
    >
        <template #showbutton="{ on }">
            <slot name="showbutton" :on="on"/>
        </template>
        <template #content>
            <v-row>
                <v-col>
                    <v-file-input
                        multiple
                        filled
                        hide-details
                        dense
                        prepend-icon="mdi-file-multiple"
                        @change="onFileLoad"
                        @click="clearInput"
                        label="Upload Files"
                        @click:clear="resetState"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <HeaderLined text="Case Settings"/>
                    <CheckboxWithIcon
                        v-model="isVisible"
                        label="Make Visible to All Users"
                    />
                    <CheckboxWithIcon
                        v-model="isWarning"
                        icon-color="warning"
                        icon-false="report_off"
                        icon-true="report"
                        tooltip-false="Does not warn creator"
                        tooltip-true="Warns creator"
                        label="Warn Creator"
                    />
                </v-col>
                <v-col>
                    <HeaderLined text="Address Import Settings"/>
                    <CheckboxWithIcon
                        v-model="propagateToCluster"
                        icon-true="mdi-hub"
                        icon-false="mdi-hub"
                        tooltip-true="Propagate Category to Clusters"
                        tooltip-false="Don't Propagate Category to Clusters"
                        label="Propagate Category to Clusters"
                    />
                    <CheckboxWithIcon
                        v-if="propagateToCluster"
                        v-model="propagateToSingletons"
                        icon-true="mdi-hub-outline"
                        icon-false="mdi-hub-outline"
                        tooltip-true="Also propagates to single-address clusters"
                        tooltip-false="Skips single-address clusters"
                        label="Include Single-Address Clusters"
                    />
                    <div class="d-flex align-center" style="gap: 8px">
                        <span class="v-label">Category: </span>
                        <v-text-field
                            dense
                            filled
                            v-model="categoryName"
                            @change="handleCategoryChange"
                            hide-details
                            clearable
                        >
                            <template #append>
                                <category-badge v-if="categoryOfChoice.name" :icon-item="categoryOfChoice"/>
                                <v-chip v-else small><i>None</i></v-chip>
                                <v-tooltip v-if="!categoryOfChoice.exists && categoryName" top>
                                    <template #activator="{ on, attrs }">
                                        <v-chip v-on="on" :attrs="attrs" small><i>New</i></v-chip>
                                    </template>
                                    <span>Category will be created.</span>
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </div>
                </v-col>
            </v-row>
            <v-row>
                <v-tabs class="mt-5" color="secondary" v-model="tabs">
                    <v-tab :disabled="invalidFiles.length === 0">
                        <v-icon :color="errorItemsColor" left size="20">fa fa-triangle-exclamation</v-icon>
                        Errors
                        <v-badge inline :content="numberFormatCompact(invalidFiles.length)"/>
                    </v-tab>
                    <v-tab :disabled="warningItems.length === 0">
                        <v-icon :color="warningItemsColor" left size="20">fa fa-exclamation</v-icon>
                        Warnings
                        <v-badge inline :content="numberFormatCompact(warningItems.length)"/>
                    </v-tab>
                    <v-tab :disabled="failedImports.length === 0">
                        <v-icon :color="failedImportsColor" left size="20">mdi-content-save-alert</v-icon>
                        Failed Imports
                        <v-badge inline :content="numberFormatCompact(failedImports.length)"/>
                    </v-tab>
                    <v-tab-item>
                        <DataGrid
                            :headers="errorHeaders"
                            :get-data="getErrorItems"
                            item_id="name"
                            computed-height
                            :items-per-page="10"
                            :bulk-operations="false"
                            :footer-props="{
                                'items-per-page-options': [5, 10, 20],
                                'items-per-page-text': 'Files per page',
                                'show-current-page': true,
                                'showFirstLastPage': true,
                            }"
                        >
                            <template #item="{ item }">
                                <td class="dynamicTruncate">
                                    <CopyableItemWithTooltip :item="item.name"/>
                                </td>
                                <td class="dynamicTruncate">
                                    <CopyableItemWithTooltip :item="item.issues"/>
                                </td>
                            </template>
                        </DataGrid>
                    </v-tab-item>
                    <v-tab-item>
                        <DataGrid
                            :headers="warningHeaders"
                            :get-data="getWarningItems"
                            computed-height
                            :bulk-operations="false"
                            :footer-props="{
                                'items-per-page-options': [5, 10, 20],
                                'show-current-page': true,
                                'showFirstLastPage': true,
                            }"
                        >
                            <template #item="{ item }">
                                <td v-for="key in Object.keys(item).filter(ObjectKey => ObjectKey !== 'patchData')" class="dynamicTruncate">
                                    <TableItemsExpandable v-if="Array.isArray(item[key])" :items="item[key]" :shown-items-amount="1">
                                        <template v-slot="{item: localItem, isInMenu}">
                                            <div v-if="isInMenu" class="px-2">
                                                <CopyableItemWithTooltip :item="localItem"/>
                                            </div>
                                            <CopyableItemWithTooltip v-else :item="localItem"/>
                                        </template>
                                    </TableItemsExpandable>
                                    <CopyableItemWithTooltip v-else :item="item[key]"/>
                                </td>
                            </template>
                        </DataGrid>
                    </v-tab-item>
                    <v-tab-item>
                        <DataGrid
                            :headers="failedImportsHeaders"
                            :get-data="getFailedImports"
                            computed-height
                            :bulk-operations="false"
                            :footer-props="{
                                'items-per-page-options': [5, 10, 20],
                                'show-current-page': true,
                                'showFirstLastPage': true,
                            }"
                        >
                            <template #item="{ item }">
                                <td class="dynamicTruncate fontMonospace">
                                    <CopyableItemWithTooltip :item="item.title"/>
                                </td>
                                <td class="text-left">
                                    <BlockchainCryptoBadge
                                        justify="start"
                                        :key="item.currency"
                                        :crypto="item.currency.toUpperCase()"
                                        :iconWidth="24"
                                    />
                                </td>
                                <td class="text-left fontMonospace">
                                    <SmartTooltipGeneric
                                        header="Addresses"
                                        :items="item.addresses"
                                        header-icon="mdi-tag-multiple"
                                    >
                                        <template #activator>
                                            <v-chip
                                                small
                                                style="cursor: pointer"
                                            >
                                                <v-icon left size="20px">mdi-tag-multiple</v-icon>
                                                {{ item.addresses?.length }}<i>&nbsp;<span v-if="item.addresses?.length === 1">address</span><span v-else>addresses</span></i>
                                            </v-chip>
                                        </template>
                                        <template #menuItem="menuItem">
                                            <RouteToAddress :address="menuItem.item" :currency="item.currency"/>
                                        </template>
                                    </SmartTooltipGeneric>
                                </td>
                                <td class="text-left fontMonospace">
                                    <SmartTooltipGeneric
                                        header="Source Files"
                                        :items="item.files"
                                        header-icon="file_copy"
                                    >
                                        <template #activator>
                                            <v-chip
                                                small
                                                style="cursor: pointer"
                                            >
                                                <v-icon left size="20px">file_copy</v-icon>
                                                {{ item.files?.length }}<i>&nbsp;file/s</i>
                                            </v-chip>
                                        </template>
                                        <template #menuItem="menuItem">
                                            <span>{{ menuItem.item }}</span>
                                        </template>
                                    </SmartTooltipGeneric>
                                </td>
                                <td class="text-left fontMonospace">
                                    <SmartTooltipBase left>
                                        <template #activator="{ on }">
                                            <v-chip
                                                v-on="on"
                                                small
                                                style="cursor: pointer"
                                            >
                                                <v-icon left size="20px" color="info">fa-solid fa-circle-info</v-icon>
                                                Errors
                                            </v-chip>
                                        </template>
                                        <template>
                                            <v-textarea
                                                readonly
                                                style="width: 700px"
                                                hide-details
                                                :value="item.error"
                                            />
                                        </template>
                                    </SmartTooltipBase>
                                </td>
                                <td class="text-left fontMonospace">
                                    <v-chip small>
                                        {{ item.action }}
                                    </v-chip>
                                </td>
                            </template>
                        </DataGrid>
                    </v-tab-item>
                </v-tabs>
            </v-row>
            <v-row>
                <v-col>
                    <v-expand-transition
                        v-for="alert in alerts"
                        :key="alert.message">
                        <v-alert
                            border="left"
                            class="mb-1"
                            dense
                            outlined
                            text
                            :type="alert.type"
                        >{{ alert.message }}
                        </v-alert>
                    </v-expand-transition>
                </v-col>
            </v-row>
            <v-row>
            </v-row>
        </template>
        <template #footer="{close}">
            <ActionConfirmDialog
                ref="confirmCloseDialogRef"
                content-text="Are you sure you want to cancel the importing process?"
                :on-confirm-action-callback="() => closeConfirmed(close)"
                modal-title="Cancel"
                />
            <v-btn small dark color="primary" @click="() => closeModal(close)">
                Close
            </v-btn>
            <div
                v-if="loading"
                style="margin: 0 100px"
                class="d-flex flex align-center justify-center flex-column"
            >
                <div class="progress-bar-text fontMonospace">
                    {{ progress.completeCount }} out of {{ progress.total }} complete
                </div>
                <v-progress-linear
                    v-model="progressPercentage"
                    height="10px"
                    rounded
                    color="info"
                />
            </div>
            <v-spacer v-else/>
                <v-btn
                    :disabled="invalidFiles.length === 0 && warningItems.length === 0 && failedImports.length === 0"
                    @click="exportErrors"
                    small
                    color="success"
                >
                    <v-icon small class="mr-1">mdi-download</v-icon>
                    Export Errors
                </v-btn>

            <ImportButton :loading="loading" :disabled="casesLoadedTotal === 0 || loading || isImportFinished" @click="onImport"/>
        </template>
    </BaseModalDialog>
</template>

<style scoped>
.v-progress-linear {
    border-radius: 12px;
    margin: 0;
}

.progress-bar-text {
    position: relative;
    font-size: 14px;
}

@keyframes fade {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
}

.progress-bar-text {
    animation: fade 2s infinite;
}
</style>